import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  MapPin,
  Upload,
  BarChart3,
  Users,
  Wallet,
  ShieldCheck,
  Package,
  CheckCircle2,
  Link2,
  Globe,
} from "lucide-react";
import hackathonCourier from "@/assets/hackathon-courier.jpg";
import hackathonParcels from "@/assets/hackathon-parcels.jpg";
import hackathonTeam from "@/assets/hackathon-team.jpg";
import hackathonDesk from "@/assets/hackathon-desk.jpg";
import hackathonWarehouse from "@/assets/hackathon-warehouse.jpg";
import hackathonLagosSkyline from "@/assets/hackathon-lagos-skyline.jpg";

const FEATURES = [
  {
    icon: MapPin,
    title: "Postcode → address autofill",
    body: "Type a NIPOST postcode anywhere an address is needed and it resolves to the real building/street instantly — backed by the live widget lookup API.",
    image: hackathonCourier,
  },
  {
    icon: Upload,
    title: "Bulk CSV validation",
    body: "Vet a hundred-row delivery import in one pass — every address geocoded and postcode-resolved, errors flagged before a single parcel is created.",
    image: hackathonDesk,
  },
  {
    icon: BarChart3,
    title: "Delivery density by zone",
    body: "Pickup and dropoff legs rolled up by NIPOST zone prefix, visualized live — the first time this operator platform has had postcode-shaped analytics.",
    image: hackathonWarehouse,
  },
  {
    icon: Users,
    title: "Zone-based driver grouping",
    body: "Unassigned deliveries cluster by postcode zone; dispatch picks one driver and assigns the whole cluster in a single click instead of one-by-one.",
    image: hackathonTeam,
  },
  {
    icon: Wallet,
    title: "Zone fee & driver suggestion",
    body: "An advisory badge previews a zone fee multiplier and the best-fit available driver — informational only, laying groundwork for real zone-based pricing.",
    image: hackathonParcels,
  },
];

function LagosSkylineBackground() {
  return (
    <>
      <img
        src={hackathonLagosSkyline}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#050b18]/40 to-[#050b18]/80" />
    </>
  );
}

export default function HackathonNipost() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      {/* Nav */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "border-b bg-background/95 shadow-sm backdrop-blur" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6">
          <div
            className={`flex items-center gap-2 font-display text-lg font-semibold transition-colors duration-300 ${
              scrolled ? "text-foreground" : "text-white"
            }`}
          >
            <Package className={`h-5 w-5 ${scrolled ? "text-primary" : "text-white"}`} /> Parcelio
          </div>
          <Link
            to="/"
            className={`inline-flex items-center gap-1.5 text-sm transition-colors duration-300 ${
              scrolled ? "text-muted-foreground hover:text-foreground" : "text-white/80 hover:text-white"
            }`}
          >
            <ArrowLeft className="h-4 w-4" /> Back to app
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pb-16 pt-14 md:pb-24 md:pt-20">
        <LagosSkylineBackground />
        <div className="relative mx-auto max-w-4xl px-4 text-center md:px-6">
          <div className="inline-flex items-center gap-2 rounded-full border-2 border-foreground bg-warning px-3 py-1 text-xs font-bold uppercase tracking-wide text-warning-foreground shadow-[2px_3px_0_0_var(--foreground)]">
            <span className="h-1.5 w-1.5 rounded-full bg-warning-foreground live-dot" />
            NIPOST Postcode Hackathon · Live pitch, Tue 27 Oct 2026
          </div>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-white md:text-6xl">
            Nigeria's address problem,
            <br />
            <span className="text-primary-foreground">solved zone by zone.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-white/80">
            Parcelio is a live, production logistics platform. This submission wires the
            National Digital Alphanumeric Postcode API directly into its core delivery flow —
            not a standalone lookup demo, but postcode-aware dispatch, analytics, and pricing.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Button size="lg" className="rounded-full text-base" asChild>
              <a href="/app/deliveries">
                Open live demo <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-2 border-white bg-transparent text-base text-white hover:bg-white hover:text-foreground" asChild>
              <a href="#features">See what we built</a>
            </Button>
          </div>
          <div className="mx-auto mt-10 grid max-w-xl grid-cols-3 gap-6 border-t-2 border-dashed border-white/25 pt-6">
            {[
              ["5", "Roadmap features shipped"],
              ["37", "NIPOST zones mapped"],
              ["1", "Real production platform"],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-semibold tracking-tight text-white">{v}</div>
                <div className="text-xs text-white/70">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem / why */}
      <section className="border-y-2 border-dashed border-border bg-surface-muted">
        <div className="mx-auto max-w-5xl px-4 py-14 text-center md:px-6">
          <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
            The problem
          </span>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight md:text-4xl">
            Nigerian addresses are free text. No standard, no zones, no verifiable location record.
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Logistics companies geocode blind — every delivery is a guess at where the street actually is.
            NIPOST's postcode system gives every address a verifiable, structured identity. We built on
            top of it to turn that identity into real operational value.
          </p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-4 py-20 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
            What we built
          </span>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight md:text-5xl">
            Five features, easy to hard, all live
          </h2>
          <p className="mt-3 text-muted-foreground">
            Every feature below is wired into the real app — not mocked, not a separate demo surface.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="surface-card surface-card-hover relative overflow-hidden p-6">
              <img src={f.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/75 to-foreground/80" />
              <div className="relative">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-foreground text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-primary-foreground">{f.title}</h3>
                <p className="mt-1 text-sm text-primary-foreground/85">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Integrity / honesty section */}
      <section className="border-y-2 border-dashed border-border bg-surface-muted">
        <div className="mx-auto max-w-5xl px-4 py-16 md:px-6">
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border-2 border-foreground bg-surface px-3 py-1 text-xs font-bold">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Built to be real, not a demo trick
              </div>
              <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight md:text-4xl">
                Append-only, non-blocking, honest about scope.
              </h2>
              <p className="mt-3 text-muted-foreground">
                Postcode enrichment never blocks delivery creation — if the API is slow or down,
                deliveries still go through and postcode fields just stay empty. The zone fee/driver
                suggestion feature is explicitly advisory only: it does not change what's charged or
                auto-assign anyone, by deliberate choice two days from pitch.
              </p>
            </div>
            <div className="space-y-3">
              {[
                "Live widget session calls against platform.postcode.gov.ng, real Bearer token",
                "Admin \"Check status\" diagnostic proves the integration isn't hardcoded",
                "Zone pricing clearly labeled advisory-only — no silent billing changes",
                "Graceful degradation tested: toggle off, creation still works, no badge",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  {t}
                </div>
              ))}
            </div>
          </div>

          <div className="relative mt-12 overflow-hidden rounded-3xl border-2 border-foreground bg-gradient-hero p-10 text-primary-foreground shadow-[6px_8px_0_0_var(--foreground)] md:p-16">
            <div className="relative max-w-2xl">
              <h2 className="font-display text-3xl font-semibold tracking-tight md:text-5xl">
                See it live at the pitch.
              </h2>
              <p className="mt-3 text-primary-foreground/85">
                Create a delivery, watch postcode auto-resolve, bulk-validate a CSV, assign a whole zone
                to a driver in one click — all on the real platform, Session 1, Tue 27 Oct 2026.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button size="lg" variant="secondary" className="rounded-full shadow-[2px_3px_0_0_var(--foreground)]" asChild>
                  <a href="/app/deliveries">
                    Open live demo <ArrowRight className="ml-1 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-4 text-center md:px-6">
          <p className="text-xs text-muted-foreground">
            Built on the National Digital Alphanumeric Postcode API · NIPOST · 3MTT Fellowship submission
          </p>
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium">Shola Omofola</span>
            <a
              href="https://www.linkedin.com/in/sholaomofola/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Link2 className="h-4 w-4" /> LinkedIn
            </a>
            <a
              href="http://sholaomofola.sixagroup.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Globe className="h-4 w-4" /> Portfolio
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
